import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import {
  AlertProvider,
  AuthProvider,
  ConfirmProvider,
  ContextMenuProvider,
  FeatureFlagProvider,
  FullscreenLoader,
  ImagePreviewProvider,
  MediaManagerProvider,
  OneSourceSnackbarProvider,
  PdfPreviewProvider,
  ProfileInfoProvider,
  UserOnlineProvider,
} from '@onesource/components';
import { EAppNameEnum } from '@onesource/schemas';
import { axiosServiceInstance, queryClient } from '@onesource/services';
import { fitColorPalette, getTheme } from '@onesource/theme';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { Provider as JotaiProvider } from 'jotai';
import { HelmetProvider } from 'react-helmet-async';

import 'src/global.css';
import Page404 from 'src/pages/page-not-found';

import { MembersFeatureFlagsDialog } from './components/feature-flags/members-feature-flags-dialog';
import { API_BASE_URL, GA4_MEASUREMENT_ID, SHOW_DEV_TOOLS } from './config';
import { FEATURE_FLAGS_FOR_UI } from './hooks/use-feature-flag-utils';
import { routeTree } from './routeTree.gen';

const theme = getTheme({
  palette: fitColorPalette,
  font: 'Figtree',
});

axiosServiceInstance.defaults.baseURL = API_BASE_URL;

const router = createRouter({
  context: {
    queryClient,
  },
  defaultPreload: 'intent',
  routeTree,
  defaultNotFoundComponent: () => {
    return <Page404 />;
  },
  Wrap: ({ children }) => {
    return (
      <HelmetProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <JotaiProvider>
            <QueryClientProvider client={queryClient}>
              <FeatureFlagProvider featureFlags={FEATURE_FLAGS_FOR_UI}>
                <AuthProvider>
                  <ContextMenuProvider>
                    <ConfirmProvider>
                      <ImagePreviewProvider>
                        <PdfPreviewProvider>
                          <MediaManagerProvider>
                            <AlertProvider>
                              <ProfileInfoProvider>
                                <OneSourceSnackbarProvider>
                                  <UserOnlineProvider app={EAppNameEnum.MEMBERS}>{children}</UserOnlineProvider>
                                  {/* Inside auth so its DB queries carry a token; reads open state from the flag context above. */}
                                  <MembersFeatureFlagsDialog />
                                </OneSourceSnackbarProvider>
                              </ProfileInfoProvider>
                            </AlertProvider>
                          </MediaManagerProvider>
                        </PdfPreviewProvider>
                      </ImagePreviewProvider>
                    </ConfirmProvider>
                  </ContextMenuProvider>
                </AuthProvider>
              </FeatureFlagProvider>
              {SHOW_DEV_TOOLS && <ReactQueryDevtools />}
            </QueryClientProvider>
            <FullscreenLoader />
          </JotaiProvider>
        </ThemeProvider>
      </HelmetProvider>
    );
  },
});

/* WARNING: Do not remove this code. it will mess up the route types */
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
  }
}

if (GA4_MEASUREMENT_ID) {
  const head = document.querySelector('head');

  if (!head?.querySelector('script[data-ga4="itbz"]')) {
    const scriptSrc = document.createElement('script');
    scriptSrc.setAttribute('data-ga4', 'itbz');
    scriptSrc.async = true;
    scriptSrc.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
    head?.append(scriptSrc);

    const scriptInit = document.createElement('script');
    scriptInit.setAttribute('data-ga4', 'itbz-init');
    scriptInit.innerText = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${GA4_MEASUREMENT_ID}', { send_page_view: false });
    `;
    head?.append(scriptInit);
  }
}

router.subscribe('onLoad', (params: { toLocation: { href: string; search: { scrollTop?: string } } }) => {
  if (params.toLocation.search.scrollTop !== '1') {
    window.scrollTo(0, 0);
  }

  if (GA4_MEASUREMENT_ID && typeof window.gtag === 'function') {
    window.gtag('event', 'page_view', {
      page_location: window.location.href,
      page_path: params.toLocation.href,
    });
  }
});

export function App() {
  return <RouterProvider router={router} />;
}
